import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useToast,
  VisuallyHiddenInput,
} from '@chakra-ui/react';
import firebase from 'firebase/app';
import moment from 'moment';
import React, { useState } from 'react';
import { Eye, EyeOff, UserPlus } from 'react-feather';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { FirebaseConfig, UserMatchType } from '../../../types/types';

interface RegisterUserProps {
  userMatch: NonNullable<UserMatchType>;
  trial: boolean;
  firebaseConfig: FirebaseConfig;
}

type FormData = {
  displayName?: string;
  password: string;
  password_repeat: string;
  telephone?: string;
};

export const RegisterUser: React.FC<RegisterUserProps> = ({
  userMatch,
  trial,
  firebaseConfig,
}) => {
  const { register, handleSubmit, formState, watch } = useForm<FormData>();
  const { errors } = formState;
  const auth = firebase.auth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const history = useHistory();
  const toast = useToast();

  const onSubmit = handleSubmit(
    ({ password, password_repeat, displayName, telephone }) => {
      setLoading(true);

      if (!userMatch || password !== password_repeat) {
        return;
      }
      const { activeMember } = userMatch;

      auth
        .createUserWithEmailAndPassword(userMatch.email, password)
        .then(
          async ({ user }) => {
            const trialExpiration = moment()
              .add(firebaseConfig.trialExpiration + 1, 'days')
              .toJSON();
            try {
              await user?.updateProfile({
                displayName:
                  !trial && activeMember
                    ? `${activeMember.firstName} ${activeMember.surName}`
                    : displayName || '',
              });
              await firebase
                .firestore()
                .collection('users')
                .doc(user?.uid)
                .set({
                  status: trial ? 'trial' : 'active',
                  trialExpiration,
                  aidooId: activeMember?.nr || '',
                  isAdmin: false,
                  telephone: telephone || '',
                  isVoucher: false,
                  courseMinutes: 0,
                  courseCount: 0,
                  adminNotes: '',
                  adminContactStatus: 'not_contacted',
                  hasConsentConfirmed: true,
                });

              await user?.sendEmailVerification();
            } catch (e) {
              try {
                await firebase
                  .firestore()
                  .collection('errors')
                  .add({
                    id: 'userDatabaseCreationCatch',
                    error: JSON.stringify(e),
                  });
              } catch (err) {
                console.error(err);
              }
            }
            setLoading(false);
            history.push('/');
            // eslint-disable-next-line no-console
            console.log('login completed');
          },
          async (reason) => {
            try {
              await firebase
                .firestore()
                .collection('errors')
                .add({
                  id: 'userCreationReject',
                  error: JSON.stringify(reason),
                });
            } catch (reasonErr) {
              console.error(reasonErr);
            }
          }
        )
        .catch(async (e: firebase.auth.AuthError) => {
          // eslint-disable-next-line no-console

          try {
            await firebase
              .firestore()
              .collection('errors')
              .add({ id: 'userCreationCatch', error: JSON.stringify(e) });
          } catch (autherr) {
            console.error(autherr);
          }

          if (e.code === 'auth/weak-password') {
            toast({
              title: 'Fehler bei der Registrierung',
              description:
                'Dein Passwort ist zu schwach, bitte nutze ein stärkeres.',
              status: 'error',
              duration: 9000,
              isClosable: true,
            });
          }
          if (e.code === 'auth/email-already-in-use') {
            toast({
              title: 'Fehler bei der Anmeldung',
              description:
                'Du hast bereits ein Konto bei uns. Melde dich bitte an.',
              status: 'error',
              duration: 9000,
              isClosable: true,
            });
          }
          toast({
            title: 'Fehler bei der Registrierung',
            description: `Ein Fehler ist aufgetreten: ${e.code}`,
            status: 'error',
            duration: 9000,
            isClosable: true,
          });
          setLoading(false);
        });
    }
  );

  return (
    <>
      {trial ? (
        <>
          <Text color="brand.500" fontWeight="bold">
            KOSTENFREIE TESTVERSION FÜR {firebaseConfig.trialExpiration} TAGE
          </Text>
          <Text mb={3} fontWeight="bold">
            {userMatch.email}
          </Text>
        </>
      ) : (
        <>
          <Text mb={3}>
            Wunderbar. Du bist ein aktives Mitglied und hast somit vollen
            Zugriff auf unser Online-Fitness-Angebot.
          </Text>
          <Text mb={6}>
            Um deine Registrierung abzuschließen, vergib dir bitte noch ein
            Passwort, damit du dich in Zukunft direkt anmelden kannst.
          </Text>
        </>
      )}
      <form onSubmit={onSubmit} autoComplete="off">
        {trial && (
          <>
            <FormControl isRequired={true} isInvalid={!!errors.password}>
              <FormLabel>Name</FormLabel>
              <Input
                autoFocus={true}
                autoComplete="name"
                focusBorderColor="brand.500"
                placeholder="Max Mustermann"
                {...register('displayName', { required: true })}
              />
            </FormControl>
            <FormControl
              mt={6}
              isRequired={true}
              isInvalid={!!errors.telephone}
            >
              <FormLabel>Telefon</FormLabel>
              <Input
                autoComplete="off"
                focusBorderColor="brand.500"
                type="tel"
                placeholder="+49123456789"
                {...register('telephone', {
                  required: true,
                  // eslint-disable-next-line max-len
                  pattern:
                    /(\(?([\d \-\)\–\+\/\(]+){6,}\)?([ .\-–\/]?)([\d]+))/,
                })}
              />
              <FormErrorMessage>
                {errors.telephone &&
                  'Bitte gib eine gültige Telefonnummer ein.'}
              </FormErrorMessage>
            </FormControl>
          </>
        )}
        <VisuallyHiddenInput
          name="email"
          readOnly={true}
          value={userMatch.email}
          autoComplete="email"
        />
        <FormControl mt={6} isRequired={true} isInvalid={!!errors.password}>
          <FormLabel>Passwort</FormLabel>
          <Input
            focusBorderColor="brand.500"
            type={showPassword ? 'text' : 'password'}
            placeholder="*******"
            {...register('password', {
              required: true,
            })}
          />
        </FormControl>
        <FormControl
          mt={6}
          isRequired={true}
          isInvalid={!!errors.password_repeat}
        >
          <FormLabel>Passwort wiederholen</FormLabel>
          <InputGroup>
            <Input
              focusBorderColor="brand.500"
              type={showPassword ? 'text' : 'password'}
              placeholder="*******"
              {...register('password_repeat', {
                required: true,
                validate: (value) =>
                  value === watch('password') ||
                  'Passwörter stimmen nicht überein',
              })}
            />
            <InputRightElement>
              <IconButton
                aria-label="Passwort anzeigen"
                icon={showPassword ? <EyeOff size="1em" /> : <Eye size="1em" />}
                size="sm"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? 'Hide' : 'Show'}
              </IconButton>
            </InputRightElement>
          </InputGroup>
          <FormErrorMessage>
            {errors.password_repeat && errors.password_repeat.message}
          </FormErrorMessage>
        </FormControl>

        <Button
          focusBorderColor="brand.500"
          colorScheme="brand"
          width="full"
          mt={6}
          type="submit"
          isLoading={loading}
          leftIcon={<Icon as={UserPlus} />}
        >
          Jetzt registrieren
        </Button>
      </form>
    </>
  );
};
